import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { useTheme } from '../../../theming/useTheme';
import { OptionalAdditionalStyle } from '../../components.utils';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';
import {
  createImageHeightCalculationMethods,
  ImageAspectRatios,
  ImageHeightCalculationMethods,
  ImageHeightCalculationMethodsTypes,
} from './image.utils';

const componentConfigThemeKey = 'imageWrapper';

interface ImageWrapperDataProps {
  imageAspectRatios: ImageAspectRatios;
  dataId?: string;
  imageHeightCalculationMethods?: ImageHeightCalculationMethods;
}

interface ImageWrapperThemeProps {
  backgroundColor: string;
}

const defaultImageHeightCalculationMethods = createImageHeightCalculationMethods('ASPECT_RATIO', 'ASPECT_RATIO', 'ASPECT_RATIO');

export const getBaseStyle = (imageAspectRatios: ImageAspectRatios, backgroundColor: string, imageHeightCalculationMethods: ImageHeightCalculationMethods) => {
  const getBaseStyleByType = (type: ImageHeightCalculationMethodsTypes) => {
    switch (type) {
      case '100%_HEIGHT':
        return {
          paddingTop: 'unset',
          height: '100%',
        };
      case 'ASPECT_RATIO':
      default:
        return {
          [MEDIA_BREAKPOINTS.large]: {
            paddingTop: `calc(100% / (${imageAspectRatios.large.x / imageAspectRatios.large.y}))`,
          },
          [MEDIA_BREAKPOINTS.medium]: {
            paddingTop: `calc(100% / (${imageAspectRatios.medium.x / imageAspectRatios.medium.y}))`,
          },
          [MEDIA_BREAKPOINTS.small]: {
            paddingTop: `calc(100% / (${imageAspectRatios.small.x / imageAspectRatios.small.y}))`,
          },
        };
    }
  };
  return StyleSheet.create({
    base: {
      backgroundColor,
      position: 'relative',
      overflow: 'hidden',
      margin: 0,
      boxSizing: 'border-box',
      flex: '1 1 100%',
      minWidth: '0%',
      [MEDIA_BREAKPOINTS.large]: {
        ...getBaseStyleByType(imageHeightCalculationMethods.large),
      },
      [MEDIA_BREAKPOINTS.medium]: {
        ...getBaseStyleByType(imageHeightCalculationMethods.medium),
      },
      [MEDIA_BREAKPOINTS.small]: {
        ...getBaseStyleByType(imageHeightCalculationMethods.small),
      },
    },
  });
};

type ImageWrapperProps = ImageWrapperDataProps & OptionalAdditionalStyle;

export const ImageWrapper: React.FunctionComponent<ImageWrapperProps> = props => {
  const { additionalStyle, dataId, children, imageHeightCalculationMethods = defaultImageHeightCalculationMethods, imageAspectRatios } = props;
  const { backgroundColor } = useTheme<ImageWrapperThemeProps>(componentConfigThemeKey);
  const baseStyle = getBaseStyle(imageAspectRatios, backgroundColor, imageHeightCalculationMethods);
  return (
    <ErrorBoundary>
      {
        additionalStyle
          ? (
            <figure data-id={dataId} className={css(additionalStyle.style)}>
              <div className={css(baseStyle.base)}>{children}</div>
            </figure>
          )
          : (
            <figure data-id={dataId} className={css(baseStyle.base)}>
              {children}
            </figure>
          )
      }
    </ErrorBoundary>
  );
};
